'use client';

import { useEffect, useRef } from 'react';
import { getDebugger } from '@/lib/debug-performance';
import { usePerformanceTracking } from '@/hooks/usePerformanceTracking';

interface PageLoadTrackerProps {
  pageType: string;
  pageName: string;
}

export default function PageLoadTracker({ pageType, pageName }: PageLoadTrackerProps) {
  const hasTracked = useRef(false);
  const mountTime = useRef(performance.now());
  const { trackOperation } = usePerformanceTracking(pageType, pageName);
  
  useEffect(() => {
    // Only track once per mount
    if (hasTracked.current) return;
    hasTracked.current = true;
    
    const debug = getDebugger(pageType);
    
    // Track actual mount time
    const actualMountTime = performance.now() - mountTime.current;
    debug.log('PAGE_MOUNT_TIME', `${pageName} mounted in ${actualMountTime.toFixed(2)}ms`);
    
    // Use requestAnimationFrame to ensure we're measuring after paint
    requestAnimationFrame(() => {
      const afterPaintTime = performance.now() - mountTime.current;
      debug.log('PAGE_AFTER_PAINT', `Page painted in ${afterPaintTime.toFixed(2)}ms`);
      
      // Track navigation timing if this is a real navigation
      const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navTiming && navTiming.loadEventEnd > 0) {
        const totalPageLoad = navTiming.loadEventEnd - navTiming.fetchStart;
        debug.log('TOTAL_PAGE_LOAD_TIME', `Total page load: ${totalPageLoad.toFixed(2)}ms`);
      }
      
      // Track Web Vitals
      if ('PerformanceObserver' in window) {
        // Observe real paint timing
        try {
          const paintObserver = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.name === 'first-contentful-paint') {
                debug.log('FCP_OBSERVED', `First Contentful Paint: ${entry.startTime.toFixed(2)}ms`);
              }
            }
          });
          paintObserver.observe({ entryTypes: ['paint'] });
          
          setTimeout(() => paintObserver.disconnect(), 5000);
        } catch (_e) {
          // Silently fail
        }
      }
    });
    
    // Simulate some real operations with actual timing
    const operation = trackOperation('HYDRATION_CHECK');
    setTimeout(() => {
      operation.end('React hydration completed');
    }, 10);
    
    // Track when images actually load
    const images = document.querySelectorAll('img');
    let loadedImages = 0;
    const totalImages = images.length;
    
    if (totalImages > 0) {
      const imageLoadStart = performance.now();
      
      images.forEach(img => {
        if (img.complete) {
          loadedImages++;
        } else {
          img.addEventListener('load', () => {
            loadedImages++;
            if (loadedImages === totalImages) {
              const imageLoadTime = performance.now() - imageLoadStart;
              debug.log('ALL_IMAGES_LOADED', `${totalImages} images loaded in ${imageLoadTime.toFixed(2)}ms`);
            }
          });
        }
      });
      
      // Check if all images were already loaded
      if (loadedImages === totalImages) {
        debug.log('IMAGES_CACHED', `${totalImages} images were already cached`);
      }
    }
    
    // Track interactive state with actual timing
    const interactiveCheck = trackOperation('INTERACTIVE_CHECK');
    
    const checkInteractive = () => {
      // Use requestIdleCallback if available, otherwise setTimeout
      const idleCallback = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 0));
      
      idleCallback(() => {
        interactiveCheck.end('Page is fully interactive');
        
        // Final timing summary
        const totalTime = performance.now() - mountTime.current;
        debug.log('PAGE_READY', `${pageName} ready in ${totalTime.toFixed(2)}ms`);
      }, { timeout: 1000 });
    };
    
    if (document.readyState === 'complete') {
      checkInteractive();
    } else {
      window.addEventListener('load', checkInteractive);
      return () => window.removeEventListener('load', checkInteractive);
    }
  }, [pageType, pageName, trackOperation])
  
  // This component doesn't render anything
  return null;
}